// Editable team assignment for one initiative (kanban ae374351; v9 shape,
// kanban 953c5a7d).
//
// Scott, 2026-09-21 check-in, after Merlin filed the production-miss
// root-cause item under Delta from untitled meetings: "You should be able to
// change which teams it's assigned to. Add, remove, whatever." And on
// 2026-10-12 (review knmtjf): "owned by a team, and relevant things from other
// teams ... someone in one meeting might say something that's relevant to
// another team. That shouldn't mean that that task is now theirs."
//
// So the editor has ONE owner (a select over the known teams, free text
// allowed) and a chip list of related teams. Save PUTs the assignment; the
// server pins it so later roadmap runs keep the human's choice, and the note
// under the chips says who pinned it and when. "Let Merlin decide" drops the
// pin.

import { useState } from 'react';
import {
  roadmapReportsApi,
  type InitiativeState,
  type TeamsOverride,
} from '../../services/roadmapReports';
import {
  mandateAreaTitle,
  mandateAreasOf,
  NO_TEAM_COLOR,
  OTHER_AREA,
  OTHER_AREA_TITLE,
  ownerTeamOf,
  relatedTeamsOf,
  type MandateLookup,
  type TeamColors,
} from './roadmapViewUtils';
import { TeamTags } from './TeamTags';
import {
  addRelated,
  assignmentSize,
  cleanOwner,
  MAX_TEAMS,
  sameAssignment,
  withArea,
  withOwner,
  type TeamAssignmentDraft,
} from './teamsEditorUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  initiative: InitiativeState;
  teamColors: TeamColors;
  /** Team names already in use on the roadmap — the owner select and the add box's suggestions. */
  knownTeams: string[];
  /** roadmapAccess.canEditTeams (every roadmap:edit-teams holder, not only admins); without it the plain tags plus the pin note. */
  canEdit: boolean;
  /** Called after a successful write so the page re-reads the initiatives. */
  onChanged?: () => void | Promise<void>;
  /** The mandate registry (v10): the owner's areas to file the item under. */
  mandates?: MandateLookup;
}

const OTHER = '__other__';

function errorMessage(err: unknown): string {
  const res = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  if (typeof res?.data?.detail === 'string') return res.data.detail;
  if (res?.status === 403) return 'You do not have permission to change teams.';
  if (res?.status === 404) return 'This initiative is no longer tracked — reload the page.';
  if (res?.status === 422) return `Up to ${MAX_TEAMS} teams including the owner, each 40 characters or fewer.`;
  return 'Could not save the teams — try again.';
}

export function PinNote({ override }: { override: TeamsOverride | null | undefined }) {
  if (!override) return null;
  return (
    <div className={styles.teamsPin} data-testid="teams-pin">
      Set by {override.set_by_email} on {override.set_at.slice(0, 10)} · Merlin keeps this on later runs
    </div>
  );
}

/** What the row says now, as a draft (the API's fold for rows nobody resolved yet). */
function currentDraft(i: InitiativeState): TeamAssignmentDraft {
  return { owner: ownerTeamOf(i), related: relatedTeamsOf(i), area: i.mandate_area ?? null };
}

export function TeamsEditor({ initiative: i, teamColors, knownTeams, canEdit, onChanged, mandates = {} }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TeamAssignmentDraft>({ owner: null, related: [], area: null });
  // The owner select's "Other…" choice reveals a free-text box.
  const [ownerOther, setOwnerOther] = useState(false);
  const [ownerText, setOwnerText] = useState('');
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Optimistic view of what was just saved, until the parent's re-read
  // catches up (the prop then matches and this becomes redundant).
  const [saved, setSaved] = useState<{ shown: InitiativeState; override: TeamsOverride | null } | null>(null);

  const shown = saved?.shown ?? i;
  const override = saved ? saved.override : i.teams_override ?? null;
  const current = currentDraft(shown);
  const nothing = !current.owner && current.related.length === 0;
  const areaTitle = mandateAreaTitle(shown, mandates);
  // The areas the DRAFT's owner can file under (none = no mandate on record).
  const draftAreas = draft.owner ? mandateAreasOf({ teams: [draft.owner], owner_team: draft.owner }, mandates) : null;

  const start = () => {
    setDraft({ ...current, related: [...current.related] });
    setOwnerOther(false);
    setOwnerText('');
    setInput('');
    setError(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const ownerChoices = (() => {
    const names = [...knownTeams];
    if (draft.owner && !names.some((t) => t.toLowerCase() === draft.owner!.toLowerCase())) names.push(draft.owner);
    return names;
  })();

  const chooseOwner = (value: string) => {
    if (value === OTHER) {
      setOwnerOther(true);
      setOwnerText('');
      return;
    }
    setOwnerOther(false);
    setDraft(withOwner(draft, value === '' ? null : value));
    setError(null);
  };
  const commitOwnerText = () => {
    const owner = cleanOwner(ownerText);
    if (!owner) return;
    setDraft(withOwner(draft, owner));
    setOwnerOther(false);
    setOwnerText('');
    setError(null);
  };

  const commitInput = () => {
    const next = addRelated(draft, input);
    if (assignmentSize(next) > MAX_TEAMS) {
      setError(`Up to ${MAX_TEAMS} teams per initiative, the owner included.`);
      return;
    }
    setDraft(next);
    setInput('');
    setError(null);
  };

  /** The draft plus whatever is still typed — nobody should lose it to a click on Save. */
  const effective = (): TeamAssignmentDraft => {
    let d = draft;
    if (ownerOther && cleanOwner(ownerText)) d = withOwner(d, ownerText);
    return addRelated(d, input);
  };

  const save = async () => {
    const next = effective();
    if (assignmentSize(next) > MAX_TEAMS) {
      setError(`Up to ${MAX_TEAMS} teams per initiative, the owner included.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await roadmapReportsApi.setInitiativeTeams(i.initiative_key, {
        owner_team: next.owner,
        related_teams: next.related,
        mandate_area: next.area ?? null,
      });
      setSaved({
        shown: {
          ...i,
          owner_team: res.owner_team,
          related_teams: res.related_teams,
          teams: res.teams,
          mandate_area: res.mandate_area ?? null,
          mandate_area_title: res.mandate_area_title ?? null,
        },
        override: res.override,
      });
      setEditing(false);
      setInput('');
      await onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    setBusy(true);
    setError(null);
    try {
      await roadmapReportsApi.releaseInitiativeTeams(i.initiative_key);
      setSaved({ shown, override: null });
      await onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const suggestions = knownTeams.filter(
    (t) => t.toLowerCase() !== (draft.owner ?? '').toLowerCase() && !draft.related.some((d) => d.toLowerCase() === t.toLowerCase()),
  );
  const dirty = editing && !sameAssignment(effective(), current);
  const ownerValue = ownerOther ? OTHER : draft.owner ?? '';

  return (
    <div className={styles.teamsEditor} data-testid="teams-editor">
      {!editing && (
        <div className={styles.teamsRow}>
          {nothing ? (
            <span className={styles.muted}>No team</span>
          ) : (
            <>
              {!current.owner && <span className={styles.muted} title="Several teams carry this item and nobody has been named as its owner yet">No owner yet ·</span>}
              <TeamTags of={shown} colors={teamColors} pinnedBy={override?.set_by_email ?? null} />
              {areaTitle && (
                <span className={styles.teamsArea} title="The owning team's mandate area this work is filed under" data-testid="teams-area">
                  · {areaTitle}
                </span>
              )}
              {!areaTitle && current.owner && mandateAreasOf(shown, mandates) && (
                <span className={styles.muted} title="Merlin files it under one of the owner's mandate areas on its next run; an admin can file it now" data-testid="teams-area-unfiled">
                  · not filed under a mandate area yet
                </span>
              )}
            </>
          )}
          {canEdit && (
            <button type="button" className={styles.respondGhost} onClick={start} disabled={busy}>
              Edit teams
            </button>
          )}
          {canEdit && override && (
            <button
              type="button"
              className={styles.respondGhost}
              onClick={() => void release()}
              disabled={busy}
              title="Drop the pin: the teams stay as they are now, but the next roadmap run may change them"
            >
              {busy ? 'Working…' : 'Let Merlin decide'}
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className={styles.teamsRow} role="group" aria-label="Edit teams">
          <label className={styles.teamsOwnerLabel} htmlFor={`owner-team-${i.initiative_key}`}>
            Owned by
          </label>
          <select
            id={`owner-team-${i.initiative_key}`}
            className={styles.teamsSelect}
            aria-label="Owning team"
            value={ownerValue}
            onChange={(e) => chooseOwner(e.target.value)}
            disabled={busy}
            autoFocus
          >
            <option value="">No task force</option>
            {ownerChoices.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value={OTHER}>Other…</option>
          </select>
          {ownerOther && (
            <input
              className={styles.teamsInput}
              aria-label="New owning team"
              placeholder="Team name…"
              value={ownerText}
              onChange={(e) => setOwnerText(e.target.value)}
              onBlur={commitOwnerText}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitOwnerText();
                }
              }}
              maxLength={40}
              disabled={busy}
            />
          )}
          {draftAreas && (
            <>
              <label className={styles.teamsOwnerLabel} htmlFor={`mandate-area-${i.initiative_key}`}>
                Files under
              </label>
              <select
                id={`mandate-area-${i.initiative_key}`}
                className={styles.teamsSelect}
                aria-label="Mandate area"
                value={draft.area ?? ''}
                onChange={(e) => setDraft(withArea(draft, e.target.value))}
                disabled={busy}
              >
                <option value="">Let Merlin file it</option>
                {draftAreas.map((a) => (
                  <option key={a.key} value={a.key}>
                    {a.title}
                  </option>
                ))}
                <option value={OTHER_AREA}>{OTHER_AREA_TITLE}</option>
              </select>
            </>
          )}
          <span className={styles.teamsOwnerLabel}>Related</span>
          <ul className={styles.teamsChips} aria-label="Related teams">
            {draft.related.map((t) => (
              <li
                key={t}
                className={`${styles.teamTag} ${styles.teamTagRelated}`}
                style={{ borderColor: teamColors[t] ?? NO_TEAM_COLOR, color: teamColors[t] ?? NO_TEAM_COLOR }}
              >
                {t}
                <button
                  type="button"
                  className={styles.teamTagRemove}
                  style={{ color: 'inherit', background: 'transparent' }}
                  aria-label={`Remove ${t}`}
                  onClick={() => setDraft({ ...draft, related: draft.related.filter((x) => x !== t) })}
                  disabled={busy}
                >
                  ×
                </button>
              </li>
            ))}
            {draft.related.length === 0 && <li className={styles.muted}>none</li>}
          </ul>
          <input
            className={styles.teamsInput}
            list={`teams-known-${i.initiative_key}`}
            aria-label="Add a related team"
            placeholder="Add a related team…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitInput();
              }
            }}
            maxLength={40}
            disabled={busy}
          />
          <datalist id={`teams-known-${i.initiative_key}`}>
            {suggestions.map((t) => <option key={t} value={t} />)}
          </datalist>
          <button type="button" className={styles.respondGhost} onClick={commitInput} disabled={busy || !input.trim()}>
            Add
          </button>
          <button type="button" className={styles.respondPrimary} onClick={() => void save()} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className={styles.respondGhost} onClick={cancel} disabled={busy}>
            Cancel
          </button>
        </div>
      )}

      <PinNote override={override} />
      {error && <div className={styles.respondError} role="alert">{error}</div>}
    </div>
  );
}
